import { type ReactNode } from 'react';

import { cn } from '@/nucleo/utilitarios';

export interface DetailWithTimelineProps {
  /** Conteúdo principal (ocupa a coluna larga). */
  children: ReactNode;
  /** Conteúdo da barra lateral — tipicamente um `<DetailSummarySidebar>`. */
  timeline: ReactNode;
  /**
   * Card-resumo em largura total, renderizado ACIMA do grid (conteúdo + timeline).
   * Quando definido, a barra lateral começa visualmente abaixo do resumo.
   */
  summary?: ReactNode;
  /** Posição da barra lateral no desktop (lg+). Padrão: "right". */
  side?: 'left' | 'right';
  /** Largura da barra lateral no breakpoint xl. Padrão: 320. */
  asideWidth?: 280 | 320;
  /** Classes extras no wrapper externo. */
  className?: string;
  /** Classes extras na coluna principal. */
  contentClassName?: string;
}

/**
 * Wrapper padrão para telas de detalhe com linha do tempo em barra lateral
 * fixa e "sticky" — cópia estrutural do protótipo
 * (`components/layout/DetailWithTimeline.tsx`). A barra lateral NÃO faz
 * parte da mesma grade de altura igual do conteúdo principal (por isso tem
 * largura fixa, não uma fração de coluna) — é isso que evita o descompasso
 * de altura/largura entre ela e os cards do conteúdo principal.
 *
 * - Mobile: coluna única — `summary` → `children` → `timeline`.
 * - lg+: se houver `summary`, ocupa a largura total no topo; abaixo, grade
 *   de 2 colunas (`1fr_280px` ou `1fr_320px`) com a barra lateral "sticky".
 */
export function DetailWithTimeline({
  children,
  timeline,
  summary,
  side = 'right',
  asideWidth = 320,
  className,
  contentClassName,
}: DetailWithTimelineProps) {
  const colunasDaGrade =
    side === 'right'
      ? asideWidth === 320
        ? 'grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] xl:grid-cols-[minmax(0,1fr)_320px]'
        : 'grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px]'
      : asideWidth === 320
        ? 'grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)]'
        : 'grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)]';

  const ordemDaBarra = side === 'right' ? 'order-2 lg:order-2' : 'order-2 lg:order-1';
  const ordemDoConteudo = side === 'right' ? 'order-1 lg:order-1' : 'order-1 lg:order-2';

  return (
    <div className={cn('space-y-4 lg:space-y-6', className)}>
      {summary && <div className="min-w-0">{summary}</div>}
      <div className={cn('grid gap-4 lg:gap-6', colunasDaGrade)}>
        <aside
          className={cn(
            'lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:self-start lg:overflow-y-auto',
            'min-w-0 space-y-4',
            'lg:rounded-xl lg:border lg:border-border/40 lg:bg-muted/30 lg:p-3',
            ordemDaBarra,
          )}
        >
          {timeline}
        </aside>
        <div className={cn('min-w-0 space-y-4', ordemDoConteudo, contentClassName)}>{children}</div>
      </div>
    </div>
  );
}
